import {
  createStylesParams,
  StyleParamType,
  wixColorParam,
} from '@wix/tpa-settings';
import { StyleType } from '@wix/editor-platform-ooi-metadata-resolver';

import { DIVIDER_TYPE } from '../../../../constants';

export type BackgroundAndBordersStylesParams = {
  appBackgroundColor: StyleParamType.Color;
  headerBackgroundColor: StyleParamType.Color;
  menuDividerType: StyleParamType.Number;
  menuDividerWidth: StyleParamType.Number;
  menuDividerColor: StyleParamType.Color;

  itemBackgroundColor: StyleParamType.Color;
  itemBorderColor: StyleParamType.Color;
  itemBorderWidth: StyleParamType.Number;

  itemCornerRadiusTopLeft: StyleParamType.Number;
  itemCornerRadiusTopRight: StyleParamType.Number;
  itemCornerRadiusBottomLeft: StyleParamType.Number;
  itemCornerRadiusBottomRight: StyleParamType.Number;
  itemCornerRadiusLinked: StyleParamType.Number;
};

export const backgroundAndBordersStylesParams =
  createStylesParams<BackgroundAndBordersStylesParams>({
    appBackgroundColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-1', 1),
      metadata: {
        type: StyleType.backgroundColor,
        defaultValue: { type: 'static', preset: 'color-1', opacity: 1 },
        ai: {
          description:
            'Background color of the overall online ordering menu container',
        },
      },
    },
    headerBackgroundColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-1', 0),
      metadata: {
        type: StyleType.backgroundColor,
        defaultValue: { type: 'static', preset: 'color-1', opacity: 0 },
        ai: {
          description:
            'Background color of the whole page header — everything above the menu navigation, covering the cover image, title, description, status badges, fulfillment picker and fulfillment info. Transparent by default so the app background shows through',
        },
      },
    },
    menuDividerType: {
      type: StyleParamType.Number,
      getDefaultValue: () => DIVIDER_TYPE.SOLID,
      metadata: {
        type: StyleType.numberEnum,
        defaultValue: { type: 'static', value: DIVIDER_TYPE.SOLID },
        ai: {
          description:
            'Controls the style of menu section dividers — 0 = solid line, 1 = dashed line, 2 = dotted line',
        },
      },
    },
    menuDividerWidth: {
      type: StyleParamType.Number,
      getDefaultValue: () => 1,
      metadata: {
        type: StyleType.borderWidth,
        defaultValue: { type: 'static', value: 1 },
        ai: {
          description:
            'Thickness in pixels of the divider line between menu sections',
        },
      },
    },
    menuDividerColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 0.3),
      metadata: {
        type: StyleType.borderColor,
        defaultValue: { type: 'static', preset: 'color-5', opacity: 0.3 },
        ai: {
          description: 'Color of the divider line between menu sections',
        },
      },
    },

    itemBackgroundColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-1', 0),
      metadata: {
        type: StyleType.backgroundColor,
        defaultValue: { type: 'static', preset: 'color-1', opacity: 0 },
        ai: {
          description:
            'Fill color of each menu item card, including unsellable items. Transparent by default so the app background shows through',
        },
      },
    },
    itemBorderColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 0.3),
      metadata: {
        type: StyleType.borderColor,
        defaultValue: { type: 'static', preset: 'color-5', opacity: 0.3 },
        ai: {
          description: 'Border color of each menu item',
        },
      },
    },
    itemBorderWidth: {
      type: StyleParamType.Number,
      getDefaultValue: () => 1,
      metadata: {
        type: StyleType.borderWidth,
        defaultValue: { type: 'static', value: 1 },
        ai: {
          description: 'Border thickness in pixels of each menu item',
        },
      },
    },

    itemCornerRadiusTopLeft: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderTopLeftRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description: 'Top-left corner radius in pixels of each menu item',
        },
      },
    },
    itemCornerRadiusTopRight: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderTopRightRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description: 'Top-right corner radius in pixels of each menu item',
        },
      },
    },
    itemCornerRadiusBottomLeft: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderBottomLeftRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description: 'Bottom-left corner radius in pixels of each menu item',
        },
      },
    },
    itemCornerRadiusBottomRight: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.borderBottomRightRadius,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description: 'Bottom-right corner radius in pixels of each menu item',
        },
      },
    },
    itemCornerRadiusLinked: {
      type: StyleParamType.Number,
      getDefaultValue: () => 0,
      metadata: {
        type: StyleType.numberEnum,
        defaultValue: { type: 'static', value: 0 },
        ai: {
          description:
            'Controls whether the menu item corner radii are linked — 1 = all corners locked together, 0 = each corner independent',
        },
      },
    },
  });
